import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Plus, UserPlus, Users } from 'lucide-react'
import notify from 'components/ui/notify'
import { initializeMyPartners, updateMyPartner } from 'slices/myPartnersSlice'
import { addPartnerToPortfolio, removePartnerFromPortfolio } from 'slices/portfolioSlice'
import { PartnerProjectCardCN, CardGridCN } from 'components/Card'
import { Button as ShadButton } from 'components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import Button from 'components/Button'
import { Spinner } from 'components/ui/spinner'
import LocationPartnersEmptyState from 'components/LocationPartnersEmptyState'
import { myPartnersService } from 'services'
import { normalizeAccessLevel } from 'utils/capabilities'
import PortfolioAddPartnerPopup from '../popups/PortfolioAddPartnerPopup'
import PortfolioPartnerAccessChooser from '../popups/PortfolioPartnerAccessChooser'

/**
 * Portfolio Partners tab — the portfolio counterpart of the Projects Partners
 * screen (screens/locations/contentPages/LocationProjectPartners.js).
 *
 * The one structural difference: adding a partner here is a two-step dialog,
 * because a portfolio grant cascades to the portfolio's locations and projects
 * and the owner chooses a level for each.
 */
const PortfolioPartners = () => {
	const dispatch = useDispatch<any>()
	const { id } = useParams()

	const [openAddPartner, setOpenAddPartner] = useState(false)
	const [selectedPartner, setSelectedPartner] = useState<any>(null)
	const [showInvitationsDialog, setShowInvitationsDialog] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)

	const portfolio = useSelector((state: any) => state.portfolios.entities[id as string])
	const userInfo = useSelector((state: any) => state.userLogin.userInfo)
	const userId = userInfo?._id

	useEffect(() => {
		dispatch(initializeMyPartners())
	}, [dispatch])

	if (!portfolio) {
		return (
			<div className="py-12">
				<Spinner />
			</div>
		)
	}

	const assetPartners = portfolio.partners ?? []
	const ownerId = portfolio.owner?._id ?? portfolio.owner
	const isOwner = String(ownerId) === String(userId)

	const pendingPartners = assetPartners.filter((partner: any) => !partner.partner?.isAccepted)

	const closeDialog = () => {
		setOpenAddPartner(false)
		setSelectedPartner(null)
	}

	const addPartnersHandler = () => {
		if (isOwner) {
			setSelectedPartner(null)
			setOpenAddPartner(true)
			return
		}
		notify({
			title: 'Add Partner Error!',
			text: `You must be the portfolio originator of ${portfolio.assetName} to add a new partner to the team.`,
			icon: 'error',
		})
	}

	const handleConfirmAccess = async ({ portfolioAccessLevel, grants }: any) => {
		const partnerName = selectedPartner?.partner?.name ?? 'This partner'
		setIsSubmitting(true)

		const action = await dispatch(
			addPartnerToPortfolio({
				portfolioId: id,
				partner: selectedPartner,
				portfolioAccessLevel,
				grants,
			})
		)

		setIsSubmitting(false)
		closeDialog()

		if (addPartnerToPortfolio.rejected.match(action)) {
			notify({
				title: 'Could not add partner',
				text: (action.payload as string) || 'Something went wrong.',
				icon: 'error',
			})
			return
		}

		const granted = grants.filter((grant: any) => grant.accessLevel !== 'none').length
		notify({
			title: 'Partner Added!',
			text: `${partnerName} is now a partner on ${portfolio.assetName}${
				granted ? ` and on ${granted} of its locations and projects` : ''
			}.`,
			icon: 'success',
		})
	}

	const handleRemovePartner = async (partner: any) => {
		const partnerUserId = partner.partnerUserId?._id ?? partner.partnerUserId
		const confirmed = await notify({
			title: 'Remove partner?',
			text: `This removes the access ${
				partner.partnerUserId?.name || 'this partner'
			} received through ${portfolio.assetName}. Any location or project they were added to directly stays untouched.`,
			icon: 'warning',
			buttons: ['Cancel', 'Remove'],
			dangerMode: true,
		})
		if (!confirmed) return

		await dispatch(removePartnerFromPortfolio({ portfolioId: id, partnerUserId }))
	}

	// The server sends the reminder, naming this portfolio (MYD-547). The
	// browser's EmailJS copy only ever reached info@.
	const handleRemindPartner = async (partner: any) => {
		const name = partner.partnerUserId?.name || 'your partner'
		try {
			await myPartnersService().resendMyPartnerInvitation(partner.partner?._id ?? partner.partner, {
				assetType: 'portfolios',
				assetId: id as string,
			})
		} catch {
			notify({
				title: 'Reminder not sent',
				text: `The reminder to ${name} could not be sent. Please try again.`,
				icon: 'error',
			})
			return
		}

		notify({
			title: 'Partner Invited!',
			text: `An email has been sent to ${name} to remind them of your partnership invitation.`,
			icon: 'success',
		})
	}

	const handleUpdatePartner = async (partner: any) => {
		await dispatch(updateMyPartner(partner))
		dispatch(initializeMyPartners())
	}

	const hasPartners = assetPartners.length > 0

	return (
		<div>
			{hasPartners && (
				<div className="flex flex-wrap justify-between items-center gap-4 mb-6">
					<div className="flex flex-col gap-1">
						<h2 className="text-2xl font-bold m-0">Partners</h2>
						<span className="text-[0.95rem] text-gray-600">
							Invite teammates and collaborators to work across this portfolio.
						</span>
					</div>
					{isOwner && (
						<div className="flex gap-3 flex-wrap">
							<Button variant="primary" onClick={addPartnersHandler}>
								<>
									<Plus className="w-4 h-4" />
									<span>Add Partner</span>
								</>
							</Button>
						</div>
					)}
				</div>
			)}

			{hasPartners ? (
				<div className="max-w-[860px] mx-auto w-full mt-4 bg-white rounded-lg border border-gray-200 p-5">
					{/* Pending invitations for this portfolio */}
					<div className="relative group rounded-lg border border-gray-200 px-4 py-3 mb-3 flex items-center justify-between">
						<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-amber-50 text-amber-600 text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
							Partners who have not yet accepted their invitation to this portfolio.
						</div>
						<span className="text-sm font-medium text-gray-700">
							Pending Invitations
							<span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
								{pendingPartners.length}
							</span>
						</span>
						<ShadButton variant="primary" isOutline className="text-xs" onClick={() => setShowInvitationsDialog(true)}>
							Manage
						</ShadButton>
					</div>

					{/* Partner cards grid */}
					<CardGridCN>
						{assetPartners.map((assetPartner: any) => {
							if (!assetPartner?.partnerUserId?._id) {
								return <h3 key={assetPartner?._id}>Partner information unavailable.</h3>
							}
							return (
								<PartnerProjectCardCN
									key={assetPartner._id}
									name={assetPartner.partnerUserId.name}
									title={assetPartner.partnerUserId.title}
									companyName={assetPartner.partnerUserId.company}
									isAccepted={assetPartner.partner?.isAccepted}
									isOwner={isOwner}
									partner={assetPartner}
									avatarUrl={assetPartner.partnerUserId.image}
									accessLevelLabel={
										normalizeAccessLevel(assetPartner.accessLevel) === 'readonly' ? 'Read Only' : 'Full Access'
									}
									handelRemindPartner={handleRemindPartner}
									handleUpdatePartner={handleUpdatePartner}
									handleRemovePartner={isOwner ? handleRemovePartner : undefined}
								/>
							)
						})}
					</CardGridCN>
				</div>
			) : (
				<LocationPartnersEmptyState onAddPartner={isOwner ? addPartnersHandler : undefined} />
			)}

			{/* Add Partner — step 1 picks the person, step 2 sets per-asset access */}
			<Dialog open={openAddPartner} onClose={closeDialog}>
				<DialogContent className="max-w-[800px]">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10 text-primary">
								{selectedPartner ? <Users className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
							</div>
							<div>
								<DialogTitle>
									{selectedPartner ? `Set access for ${selectedPartner.partner?.name}` : 'Add Partner'}
								</DialogTitle>
								<DialogDescription>
									{selectedPartner
										? `Choose what they can see across ${portfolio.assetName}`
										: 'Add a partner to this portfolio'}
								</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						{isSubmitting ? (
							<div className="py-12">
								<Spinner />
							</div>
						) : selectedPartner ? (
							<PortfolioPartnerAccessChooser
								portfolioId={id as string}
								partner={selectedPartner}
								onCancel={() => setSelectedPartner(null)}
								onConfirm={handleConfirmAccess}
							/>
						) : (
							<PortfolioAddPartnerPopup portfolio={portfolio} onSelectPartner={setSelectedPartner} />
						)}
					</div>
				</DialogContent>
			</Dialog>

			{/* Pending Invitations Dialog */}
			<Dialog open={showInvitationsDialog} onClose={() => setShowInvitationsDialog(false)}>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10 text-primary">
								<UserPlus className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>Pending Invitations</DialogTitle>
								<DialogDescription>View partner invitations</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="divide-y divide-gray-100">
						{pendingPartners.length === 0 ? (
							<div className="px-5 py-8 text-center text-sm text-gray-400">No pending invitations</div>
						) : (
							pendingPartners.map((assetPartner: any) => (
								<div key={assetPartner._id} className="flex items-center gap-4 px-5 py-4">
									<Avatar className="h-10 w-10 flex-shrink-0">
										<AvatarImage src={assetPartner.partnerUserId?.image} alt={assetPartner.partnerUserId?.name || ''} />
										<AvatarFallback className="text-sm bg-gray-100 text-gray-600">
											{(assetPartner.partnerUserId?.name || '?').charAt(0).toUpperCase()}
										</AvatarFallback>
									</Avatar>
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium text-gray-900 truncate">{assetPartner.partnerUserId?.name}</p>
										<p className="text-sm text-gray-500 truncate">
											{assetPartner.partnerUserId?.company}
											{assetPartner.partnerUserId?.title && (
												<span className="text-xs text-gray-400">
													{' · '}
													{assetPartner.partnerUserId.title}
												</span>
											)}
										</p>
									</div>
									<ShadButton
										variant="primary"
										isOutline
										className="flex-shrink-0 text-xs"
										onClick={() => handleRemindPartner(assetPartner)}
									>
										Remind
									</ShadButton>
								</div>
							))
						)}
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default PortfolioPartners
